// @docs:
//   - docs/wip/CREW_PAPER_REQUESTS.md  § Crew sign-in (papers-only account), § API contract (/me)
// ==============================================
// /papers — "My papers": the landing for a papers-only account, and a way in
// for anyone who has been sent a request. The requests sent to me, what I have
// shared and until when, and my own papers (view, delete). No plan, device or
// billing pages here — that is the point of it.
// ==============================================

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../../services/api';
import { useAuth } from '../../../providers/AuthProvider';
import { useLogout } from '../../../hooks/useLogout';
import { apiError } from '../apiError';
import { crewTypeLabel } from '../itemDisplay';
import { accessEndDate, firstName, fmtDay, passageLabel } from '../dates';
import type { MyPaper, MyRequestView } from '../types';
import { C, ConfirmDialog, CrewShell, ErrorNote, Loading, SecondaryButton, Chevron } from '../ui';
import { PreviewModal } from '../../../components/papers/shared';

const CLOSED = new Set(['closed', 'cancelled']);

function RequestRow({ r }: { r: MyRequestView }) {
  const passage = passageLabel(r.fromPlace, r.toPlace);
  const closed = CLOSED.has(r.status);
  const state = closed ? (r.status === 'cancelled' ? 'Cancelled' : 'Closed') : r.done ? 'All done' : `${r.counts.provided} of ${r.counts.items} ready`;
  return (
    <Link to={`/papers/requests/${r.id}`} className={`flex min-h-[72px] items-center gap-3.5 rounded-[14px] border border-[#1d3a70] bg-[#0b1f45] p-4 text-[#e8eef8] ${C.focus}`}>
      <span className="flex min-w-0 flex-grow flex-col gap-0.5">
        <span className="text-[17px] font-semibold">{r.requesterName ?? 'A skipper'}{passage ? ` · ${passage}` : ''}</span>
        <span className={`text-sm ${r.done ? 'text-[#6ee7a2]' : closed ? C.muted : 'text-[#9fcbff]'}`}>{state}{!closed && r.dueOn ? ` · due ${fmtDay(r.dueOn)}` : ''}</span>
      </span>
      <Chevron />
    </Link>
  );
}

export function MyPapersPage() {
  const { user } = useAuth();
  const signOut = useLogout();
  const qc = useQueryClient();
  const access = useQuery({ queryKey: ['papers-me', 'access'], queryFn: () => api.papersMeAccess(), retry: false });
  const requests = useQuery({ queryKey: ['papers-me', 'requests'], queryFn: () => api.papersMyRequests(), retry: false });
  const papers = useQuery({ queryKey: ['papers-me', 'documents'], queryFn: () => api.papersMyPapers(), retry: false });
  const [deleting, setDeleting] = useState<MyPaper | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [preview, setPreview] = useState<{ url: string; mime: string; name: string } | null>(null);

  const open = async (d: MyPaper) => {
    setError(null);
    try {
      const doc = await api.papersMyDocument(d.id);
      const f = doc.files?.[0];
      if (!f) { setError('This paper has no file — it holds only its details.'); return; }
      const blob = await api.papersMyFileBlob(d.id, f.id);
      setPreview({ url: URL.createObjectURL(blob), mime: f.mime, name: f.fileName });
    } catch (err) { setError(apiError(err, 'That paper couldn’t be opened.').message); }
  };

  const remove = async () => {
    if (!deleting) return;
    setBusy(true); setError(null);
    try {
      await api.papersDeleteMyPaper(deleting.id);
      setDeleting(null);
      qc.invalidateQueries({ queryKey: ['papers-me'] });
    } catch (err) {
      setError(apiError(err, 'That paper couldn’t be deleted.').message);
      setDeleting(null);
    } finally { setBusy(false); }
  };

  const loading = requests.isLoading || papers.isLoading;
  const loadError = requests.error || papers.error;
  const hasDashboard = !!access.data && (access.data.hasPlan || access.data.vesselCount > 0);
  const active = (d: MyPaper) => d.grants.filter((g) => g.active);

  return (
    <CrewShell right={user?.name ?? user?.email}>
      <div className="flex flex-col gap-1.5">
        <h1 className="m-0 text-[26px] font-bold leading-tight">My papers</h1>
        <p className={`m-0 text-base ${C.soft}`}>Your own papers, and who you’ve shared them with.</p>
      </div>
      {loading && <Loading />}
      {loadError && <ErrorNote>{apiError(loadError, 'Your papers could not be loaded.').message}</ErrorNote>}
      <ErrorNote>{error}</ErrorNote>

      {requests.data && requests.data.length > 0 && (
        <section className="flex flex-col gap-2.5" aria-labelledby="mp-requests">
          <h2 id="mp-requests" className={`m-0 ${C.label}`}>Requests for your papers</h2>
          {requests.data.map((r) => <RequestRow key={r.id} r={r} />)}
        </section>
      )}

      {papers.data && (
        <section className="flex flex-col gap-2.5" aria-labelledby="mp-papers">
          <h2 id="mp-papers" className={`m-0 ${C.label}`}>Your papers</h2>
          {papers.data.length === 0 && <p className={`m-0 text-[15px] ${C.muted}`}>Nothing here yet. When a skipper asks for your papers, the ones you add are kept here for next time.</p>}
          <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
            {papers.data.map((d) => (
              <li key={d.id} className={`${C.card} flex flex-col gap-2 p-4`}>
                <div className="flex flex-col gap-0.5">
                  <span className="text-[17px] font-semibold">{d.title}</span>
                  <span className={`text-sm ${C.muted}`}>{crewTypeLabel(d.type)}{d.expiresOn ? ` · expires ${fmtDay(d.expiresOn)} ${d.expiresOn.slice(0, 4)}` : ''}</span>
                  {active(d).map((g) => (
                    <Link key={g.id} to={`/papers/requests/${g.requestId}/shared`} className={`text-sm ${C.link}`}>
                      Shared with {firstName(g.granteeName)} until {fmtDay(accessEndDate(g.expiresAt, Intl.DateTimeFormat().resolvedOptions().timeZone))}
                    </Link>
                  ))}
                </div>
                <div className="flex gap-2.5">
                  {(d.fileCount ?? (d.file ? 1 : 0)) > 0 && <SecondaryButton className="flex-1" onClick={() => void open(d)}>View</SecondaryButton>}
                  <SecondaryButton className="flex-1" tone="danger" onClick={() => setDeleting(d)}>Delete</SecondaryButton>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <div className={`mt-auto flex flex-col gap-2 pt-2 text-[15px] ${C.muted}`}>
        {hasDashboard && <Link to="/dashboard" className={`min-h-[44px] self-start py-2 ${C.link}`}>Go to the MyPolaris dashboard</Link>}
        <button type="button" onClick={() => void signOut()} className={`min-h-[44px] self-start py-2 text-left ${C.link} ${C.focus}`}>Sign out</button>
      </div>

      {deleting && (
        <ConfirmDialog title={`Delete ${deleting.title}?`} confirmLabel="Delete" danger busy={busy} onConfirm={() => void remove()} onCancel={() => setDeleting(null)}>
          {active(deleting).length
            ? `It’s shared with ${firstName(active(deleting)[0].granteeName)} right now — withdraw access first, then delete it.`
            : 'It’s removed from your papers for good. Anyone you shared it with already can’t see it any more.'}
        </ConfirmDialog>
      )}
      {preview && <PreviewModal url={preview.url} mime={preview.mime} name={preview.name} onClose={() => { URL.revokeObjectURL(preview.url); setPreview(null); }} />}
    </CrewShell>
  );
}

export default MyPapersPage;
